import { useState, useEffect } from 'react';
import { X, Cookie, Shield, Settings, Check } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

interface CookieConsentProps {
  className?: string;
}

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export function CookieConsent({ className }: CookieConsentProps) {
  const [showConsent, setShowConsent] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already given consent
    const hasConsented = localStorage.getItem('cookie_consent');
    if (!hasConsented) {
      // Show consent banner after a short delay
      const timer = setTimeout(() => {
        setShowConsent(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    localStorage.setItem('cookie_consent', JSON.stringify(allAccepted));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    
    setShowConsent(false);
    setShowSettings(false);
    
    // Initialize analytics/tracking here based on preferences
    initializeTracking(allAccepted);
  };

  const handleRejectAll = () => {
    const rejected = {
      necessary: true, // Necessary cookies can't be rejected
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    localStorage.setItem('cookie_consent', JSON.stringify(rejected));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    
    setShowConsent(false);
    setShowSettings(false);
    
    initializeTracking(rejected);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie_consent', JSON.stringify(preferences));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    
    setShowConsent(false);
    setShowSettings(false);
    
    initializeTracking(preferences);
  };

  const initializeTracking = (prefs: CookiePreferences) => {
    // Initialize tracking services based on preferences
    if (prefs.analytics) {
      // Initialize Google Analytics or other analytics
      console.log('Analytics cookies enabled');
    }
    if (prefs.marketing) {
      // Initialize marketing/advertising cookies
      console.log('Marketing cookies enabled');
    }
    if (prefs.preferences) {
      // Initialize preference cookies
      console.log('Preference cookies enabled');
    }
  };

  const updatePreference = (key: keyof CookiePreferences, value: boolean) => {
    if (key === 'necessary') return; // Can't change necessary cookies
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  if (!showConsent) return null;

  return (
    <div className={cn("fixed inset-0 z-50 bg-black/50 backdrop-blur-sm", className)}>
      <div className="fixed bottom-0 left-0 right-0 p-4 sm:p-6">
        <div className="mx-auto max-w-4xl">
          <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
            {!showSettings ? (
              <>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center">
                        <Cookie className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-slate-900">Cookie Preferences</CardTitle>
                        <p className="text-sm text-slate-600">We respect your privacy</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                        <Shield className="h-3 w-3 mr-1" />
                        GDPR Compliant
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    <p className="text-sm text-slate-700 leading-relaxed">
                      We use cookies to enhance your experience, analyze site usage, and assist in our marketing efforts. 
                      By clicking "Accept All", you consent to our use of cookies. You can manage your preferences or 
                      learn more in our <a href="/privacy-policy" className="text-blue-600 hover:underline font-medium">Privacy Policy</a>.
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <Button 
                        onClick={handleAcceptAll}
                        className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Accept All Cookies
                      </Button>
                      <Button 
                        onClick={handleRejectAll}
                        variant="outline"
                        className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50 font-medium py-3 rounded-xl"
                      >
                        Reject All
                      </Button>
                      <Button 
                        onClick={() => setShowSettings(true)}
                        variant="outline"
                        className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50 font-medium py-3 rounded-xl"
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        Customize
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            ) : (
              <>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center">
                        <Settings className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-slate-900">Cookie Settings</CardTitle>
                        <p className="text-sm text-slate-600">Manage your cookie preferences</p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowSettings(false)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-6">
                    <div className="space-y-4">
                      {/* Necessary Cookies */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">Necessary Cookies</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Essential for website functionality. Cannot be disabled.
                          </p>
                        </div>
                        <Switch 
                          checked={preferences.necessary}
                          disabled={true}
                          className="ml-4"
                        />
                      </div>

                      {/* Analytics Cookies */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">Analytics Cookies</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Help us understand how you use our website to improve performance.
                          </p>
                        </div>
                        <Switch 
                          checked={preferences.analytics}
                          onCheckedChange={(checked) => updatePreference('analytics', checked)}
                          className="ml-4"
                        />
                      </div>

                      {/* Marketing Cookies */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">Marketing Cookies</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Used to deliver personalized advertisements and measure effectiveness.
                          </p>
                        </div>
                        <Switch 
                          checked={preferences.marketing}
                          onCheckedChange={(checked) => updatePreference('marketing', checked)}
                          className="ml-4"
                        />
                      </div>

                      {/* Preference Cookies */}
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                        <div className="flex-1">
                          <h4 className="font-semibold text-slate-900">Preference Cookies</h4>
                          <p className="text-sm text-slate-600 mt-1">
                            Remember your settings and preferences for a better experience.
                          </p>
                        </div>
                        <Switch 
                          checked={preferences.preferences}
                          onCheckedChange={(checked) => updatePreference('preferences', checked)}
                          className="ml-4"
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                      <Button 
                        onClick={handleSavePreferences}
                        className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-medium py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Save Preferences
                      </Button>
                      <Button 
                        onClick={handleAcceptAll}
                        variant="outline"
                        className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50 font-medium py-3 rounded-xl"
                      >
                        Accept All
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
} 